import { type ReferenceData } from '@apps/web/components/phoebe_chat/assistant_response_types';
import { isRecord } from '@apps/web/components/phoebe_chat/record_utils';
import { type ToolExecutionEntry } from '@apps/web/components/phoebe_chat/transcript_types';
import { resolveEntityReferences } from '@apps/web/lib/entity_references';

// New V3 runs advertise request_user_decision. Keep both previous names so
// saved V2 and V3 runs continue to share the same durable answer flow.
export const ASK_USER_QUESTION_TOOL_NAME = 'ask_user_question';
export const REQUEST_USER_DECISION_TOOL_NAME = 'request_user_decision';
export const SHOW_DYNAMIC_UI_TOOL_NAME = 'show_dynamic_ui';

const USER_INPUT_TOOL_NAMES = new Set<string>([
    ASK_USER_QUESTION_TOOL_NAME,
    REQUEST_USER_DECISION_TOOL_NAME,
    SHOW_DYNAMIC_UI_TOOL_NAME,
]);

export function isUserInputToolName(toolName: unknown): toolName is string {
    return typeof toolName === 'string' && USER_INPUT_TOOL_NAMES.has(toolName);
}

type QuestionAnswerItemPayload = {
    readonly question_id: string;
    readonly answer?: string | Array<string> | null;
    readonly skipped: boolean;
};
export type QuestionAnswerPayload = {
    readonly answer?: string | Array<string> | null;
    readonly answers?: Array<QuestionAnswerItemPayload>;
    readonly cancelled?: boolean;
};

type UserQuestionInputType =
    | 'text'
    | 'single_choice'
    | 'multi_choice'
    | 'yes_no';

export type UserQuestionItem = {
    readonly id: string;
    readonly question: string;
    // Display variants carry entity references enriched to [[type:uuid]]
    // tokens; raw `question`/`choices` drive the submitted values.
    readonly displayQuestion: string;
    readonly inputType: UserQuestionInputType;
    readonly choices: Array<string>;
    readonly choiceDescriptions: Array<string | null>;
    readonly displayChoices: Array<string>;
    readonly placeholder: string | null;
    readonly searchable: boolean;
    readonly allowOther: boolean;
    readonly trainingContext: string | null;
    readonly header: string | null;
};

export type UserQuestionAnswerDraft = string | Array<string>;

export type UserQuestionResolvedAnswer = {
    readonly questionId: string;
    readonly question: string;
    readonly answerText: string;
    readonly skipped: boolean;
};

export type TeachPhoebeRecordedTraceEntry = {
    readonly questionId: string;
    readonly questionDisplay: string;
    readonly answerDisplay: string;
    readonly skipped: boolean;
};

const USER_QUESTION_CHOICE_LABELS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

const USER_QUESTION_HEADER_MAX_LENGTH = 12;

type NormalizedChoice = {
    readonly value: string;
    readonly description: string | null;
};

// Choices arrive either as plain strings (legacy runs) or as
// `{label, description?}` records; the label is the submitted value.
function normalizeChoices(rawChoices: unknown): Array<NormalizedChoice> {
    if (!Array.isArray(rawChoices)) {
        return [];
    }
    const choices: Array<NormalizedChoice> = [];
    for (const rawChoice of rawChoices) {
        if (typeof rawChoice === 'string') {
            if (rawChoice.trim().length > 0) {
                choices.push({ value: rawChoice, description: null });
            }
            continue;
        }
        if (!isRecord(rawChoice)) {
            continue;
        }
        const label = rawChoice['label'];
        if (typeof label !== 'string' || label.trim().length === 0) {
            continue;
        }
        const description = rawChoice['description'];
        choices.push({
            value: label,
            description:
                typeof description === 'string' && description.trim()
                    ? description.trim()
                    : null,
        });
    }
    return choices;
}

function normalizeUserQuestionItem(
    rawQuestion: Record<string, unknown>,
    displayQuestion: Record<string, unknown>,
    index: number,
): UserQuestionItem {
    const rawInputType = rawQuestion['input_type'];
    const inputType: UserQuestionInputType =
        rawInputType === 'single_choice' ||
        rawInputType === 'multi_choice' ||
        rawInputType === 'yes_no'
            ? rawInputType
            : 'text';
    const normalizedChoices = normalizeChoices(rawQuestion['choices']);
    const choices = normalizedChoices.map((choice) => choice.value);
    const displayChoicesSource = normalizeChoices(
        displayQuestion['choices'],
    ).map((choice) => choice.value);
    // Display choices must pair 1:1 with raw choices; on any mismatch fall
    // back to raw so labels can never desync from submitted values.
    const displayChoices =
        displayChoicesSource.length === choices.length
            ? displayChoicesSource
            : choices;
    const questionText =
        typeof rawQuestion['question'] === 'string' &&
        rawQuestion['question'].trim()
            ? rawQuestion['question'].trim()
            : 'Phoebe needs your answer to continue.';
    const rawQuestionId = rawQuestion['id'] ?? rawQuestion['question_id'];
    const rawHeader = rawQuestion['header'];
    return {
        id:
            typeof rawQuestionId === 'string' && rawQuestionId.trim()
                ? rawQuestionId.trim()
                : `q${index + 1}`,
        question: questionText,
        displayQuestion:
            typeof displayQuestion['question'] === 'string' &&
            displayQuestion['question'].trim()
                ? displayQuestion['question'].trim()
                : questionText,
        inputType,
        choices,
        choiceDescriptions: normalizedChoices.map(
            (choice) => choice.description,
        ),
        displayChoices,
        placeholder:
            typeof rawQuestion['placeholder'] === 'string' &&
            rawQuestion['placeholder'].trim()
                ? rawQuestion['placeholder'].trim()
                : null,
        searchable:
            inputType === 'single_choice' && rawQuestion['searchable'] === true,
        allowOther: rawQuestion['allow_other'] !== false,
        trainingContext:
            typeof rawQuestion['training_context'] === 'string' &&
            rawQuestion['training_context'].trim()
                ? rawQuestion['training_context'].trim()
                : null,
        header:
            typeof rawHeader === 'string' && rawHeader.trim()
                ? rawHeader.trim().slice(0, USER_QUESTION_HEADER_MAX_LENGTH)
                : null,
    };
}

export function getUserQuestions(
    entry: ToolExecutionEntry,
): Array<UserQuestionItem> {
    const args = isRecord(entry.arguments) ? entry.arguments : {};
    const displayArgs = isRecord(entry.displayArguments)
        ? entry.displayArguments
        : args;
    const rawQuestions = args['questions'];
    const rawDisplayQuestions = displayArgs['questions'];
    if (Array.isArray(rawQuestions) && rawQuestions.length > 0) {
        return rawQuestions
            .filter((question): question is Record<string, unknown> =>
                isRecord(question),
            )
            .map((question, index) => {
                const displayQuestion = Array.isArray(rawDisplayQuestions)
                    ? rawDisplayQuestions[index]
                    : undefined;
                return normalizeUserQuestionItem(
                    question,
                    isRecord(displayQuestion) ? displayQuestion : question,
                    index,
                );
            });
    }

    return [normalizeUserQuestionItem(args, displayArgs, 0)];
}

// A "What do you want next?" card: one single-choice question tagged
// next_action whose choices are next steps Phoebe carries out. Rendered as
// right-aligned message bubbles instead of the standard question layout.
export function isNextActionQuestionSet(
    questions: ReadonlyArray<UserQuestionItem>,
): boolean {
    const first = questions[0];
    return (
        questions.length === 1 &&
        first !== undefined &&
        first.trainingContext === 'next_action' &&
        first.inputType === 'single_choice' &&
        first.choices.length > 0
    );
}

export function choiceLabel(index: number): string {
    return USER_QUESTION_CHOICE_LABELS[index] ?? String(index + 1);
}

export function getQuestionChoiceRows(question: UserQuestionItem): Array<{
    readonly label: string;
    readonly value: string;
    readonly description: string | null;
}> {
    if (question.inputType === 'yes_no') {
        return [
            { label: 'Yes', value: 'yes', description: null },
            { label: 'No', value: 'no', description: null },
        ];
    }
    // Enriched display label; raw value is what gets submitted.
    return question.choices.map((choice, index) => ({
        label: question.displayChoices[index] ?? choice,
        value: choice,
        description: question.choiceDescriptions[index] ?? null,
    }));
}

// Plain-text reference resolution for contexts that cannot nest
// interactive badges (choice-row buttons, resolved answer text).
export function replaceReferenceTokensWithNames(
    content: string,
    references: Array<ReferenceData> | undefined,
): string {
    return resolveEntityReferences(content, references);
}

export function getResolvedQuestionAnswers(
    entry: ToolExecutionEntry,
    questions: ReadonlyArray<UserQuestionItem>,
): Array<UserQuestionResolvedAnswer> {
    const output = isRecord(entry.output) ? entry.output : {};
    const cancelled =
        output['status'] === 'cancelled' || output['cancelled'] === true;
    if (cancelled) {
        const firstQuestion = questions[0];
        return [
            {
                questionId: firstQuestion?.id ?? 'q1',
                question: firstQuestion?.question ?? 'Phoebe asked a question.',
                answerText: 'Cancelled',
                skipped: false,
            },
        ];
    }

    const rawAnswers = output['answers'];
    if (Array.isArray(rawAnswers) && rawAnswers.length > 0) {
        return rawAnswers
            .filter((answer): answer is Record<string, unknown> =>
                isRecord(answer),
            )
            .map((answer, index) => {
                const fallbackQuestion = questions[index];
                const rawQuestionId = answer['question_id'];
                const rawQuestion = answer['question'];
                const rawAnswerText = answer['answer_text'];
                return {
                    questionId:
                        typeof rawQuestionId === 'string' &&
                        rawQuestionId.trim()
                            ? rawQuestionId.trim()
                            : (fallbackQuestion?.id ?? `q${index + 1}`),
                    question:
                        typeof rawQuestion === 'string' && rawQuestion.trim()
                            ? rawQuestion.trim()
                            : (fallbackQuestion?.question ??
                              'Phoebe asked a question.'),
                    answerText:
                        typeof rawAnswerText === 'string' &&
                        rawAnswerText.trim()
                            ? rawAnswerText.trim()
                            : answer['skipped'] === true
                              ? 'Skipped'
                              : 'Answered',
                    skipped: answer['skipped'] === true,
                };
            });
    }

    const firstQuestion = questions[0];
    const answer = output['answer'];
    const skipped = output['skipped'] === true;
    return [
        {
            questionId: firstQuestion?.id ?? 'q1',
            question: firstQuestion?.question ?? 'Phoebe asked a question.',
            answerText: skipped
                ? 'Skipped'
                : Array.isArray(answer)
                  ? answer.join(', ')
                  : typeof answer === 'string' && answer.trim()
                    ? answer.trim()
                    : 'Answered',
            skipped,
        },
    ];
}

// A resolved answer echoes RAW choice strings; swap in the enriched
// display labels so answered cards show names too. Free-text (Other…)
// parts pass through untouched.
export function displayResolvedAnswerText(
    answer: UserQuestionResolvedAnswer,
    question: UserQuestionItem | undefined,
): string {
    if (!question || question.choices.length === 0 || answer.skipped) {
        return answer.answerText;
    }
    const displayByRaw = new Map(
        question.choices.map((choice, index) => [
            choice,
            question.displayChoices[index] ?? choice,
        ]),
    );
    const direct = displayByRaw.get(answer.answerText);
    if (direct !== undefined) {
        return direct;
    }
    if (question.inputType === 'multi_choice') {
        return answer.answerText
            .split(', ')
            .map((part) => displayByRaw.get(part) ?? part)
            .join(', ');
    }
    return answer.answerText;
}

type RecordedTraceAnswerItem = {
    readonly question_id: string;
    readonly answer?: string | Array<string> | null;
    readonly skipped: boolean;
};

function formatSubmittedAnswerDisplay(
    question: UserQuestionItem | undefined,
    answer: string | Array<string> | null | undefined,
): string {
    if (answer === null || answer === undefined) {
        return 'Answered';
    }
    if (Array.isArray(answer)) {
        if (!question || question.choices.length === 0) {
            return answer.join(', ');
        }
        const displayByRaw = new Map(
            question.choices.map((choice, index) => [
                choice,
                question.displayChoices[index] ?? choice,
            ]),
        );
        return answer.map((part) => displayByRaw.get(part) ?? part).join(', ');
    }
    if (!question || question.choices.length === 0) {
        return answer;
    }
    const choiceIndex = question.choices.indexOf(answer);
    if (choiceIndex >= 0) {
        return question.displayChoices[choiceIndex] ?? answer;
    }
    return answer;
}

export function buildRecordedTraceEntriesFromSubmission(
    questions: ReadonlyArray<UserQuestionItem>,
    entry: ToolExecutionEntry,
    answers: ReadonlyArray<RecordedTraceAnswerItem>,
): Array<TeachPhoebeRecordedTraceEntry> {
    return answers.map((item, index) => {
        const question =
            questions.find((candidate) => candidate.id === item.question_id) ??
            questions[index];
        const questionDisplay = replaceReferenceTokensWithNames(
            question?.displayQuestion ?? 'Phoebe asked a question.',
            entry.references,
        );
        const skipped = item.skipped;
        const answerDisplay = skipped
            ? ''
            : replaceReferenceTokensWithNames(
                  formatSubmittedAnswerDisplay(question, item.answer),
                  entry.references,
              );
        return {
            questionId: item.question_id,
            questionDisplay,
            answerDisplay,
            skipped,
        };
    });
}

export function buildRecordedTraceEntriesFromResolved(
    questions: ReadonlyArray<UserQuestionItem>,
    entry: ToolExecutionEntry,
    resolvedAnswers: ReadonlyArray<UserQuestionResolvedAnswer>,
): Array<TeachPhoebeRecordedTraceEntry> {
    return resolvedAnswers.map((answer) => {
        const question = questions.find(
            (item) => item.id === answer.questionId,
        );
        return {
            questionId: answer.questionId,
            questionDisplay: replaceReferenceTokensWithNames(
                question?.displayQuestion ?? answer.question,
                entry.references,
            ),
            answerDisplay: replaceReferenceTokensWithNames(
                displayResolvedAnswerText(answer, question),
                entry.references,
            ),
            skipped: answer.skipped,
        };
    });
}
